import { ApiError } from '../../api/http';
import { plainFailure } from '../../api/plain-failure';

/**
 * Several addresses in the invite box (IMPLEMENTATION.md row 48, bulk invite): pasted from a
 * spreadsheet column, a mail client's To line or typed with commas. One request per address, so
 * a bad one refuses only itself.
 */

/** Addresses out of free text: split on commas, semicolons, whitespace and newlines; `Name <a@b>` keeps the address; duplicates once. */
export function parseEmails(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split(/[,;\s]+/)) {
    const angle = /<([^>]+)>/.exec(raw);
    const email = (angle?.[1] ?? raw).trim().toLowerCase();
    if (!email.includes('@') || seen.has(email)) continue;
    seen.add(email);
    out.push(email);
  }
  return out;
}

/** What was typed that is not an address (no "@"): nobody is invited by it, and the form says so. */
export function notAddresses(text: string): string[] {
  const seen = new Set<string>();
  // `Name <a@b>` is an address with its name; the name is not something that was left out.
  const bare = text.replace(/[^,;<\n]*<([^>]*)>/g, ' $1 ');
  for (const token of bare.split(/[,;\s]+/)) {
    if (token && !token.includes('@')) seen.add(token);
  }
  return [...seen];
}

/** "Not an email address, so not invited: jane, bob." */
export function notAddressLine(tokens: string[]): string | null {
  if (tokens.length === 0) return null;
  return `Not an email address, so not invited: ${tokens.join(', ')}.`;
}

/** Someone already invited, whose invitation's role an invite with another role changes. */
export interface Reinvite {
  email: string;
  was: string;
  now: string;
}

/**
 * The addresses among `emails` that already hold an invitation with a different role: inviting them
 * again changes it, which the list did without a word (pass 3, 2026-10-06).
 */
export function reinvited(
  emails: string[],
  role: string,
  people: readonly { email: string; role: string; pending: boolean }[],
): Reinvite[] {
  const pending = new Map(
    people.filter((p) => p.pending).map((p) => [p.email.toLowerCase(), p.role] as const),
  );
  return emails
    .map((email) => ({ email, was: pending.get(email) ?? role, now: role }))
    .filter((r) => r.was !== r.now);
}

/** What went wrong for one address. */
export interface InviteRefusal {
  email: string;
  message: string;
}

/** The company has sent its day's invitations (429 `invite_limit`): no later address would go either. */
const dayUsedUp = (e: unknown): boolean => e instanceof ApiError && e.code === 'invite_limit';

/**
 * Invites each address in turn; the refusals come back, the rest are on the list. Once the company's
 * day of invitations is used up, the addresses still to go are refused with the same sentence and
 * nothing more is asked.
 */
export async function inviteEach(
  emails: string[],
  send: (email: string) => Promise<unknown>,
): Promise<InviteRefusal[]> {
  const refused: InviteRefusal[] = [];
  for (const [i, email] of emails.entries()) {
    try {
      await send(email);
    } catch (e) {
      // The session ended: that is the whole app's to say (sign in, and come back here), not one address's.
      if (e instanceof ApiError && e.unauthenticated) throw e;
      const message = plainFailure(e);
      refused.push({ email, message });
      if (!dayUsedUp(e)) continue;
      for (const rest of emails.slice(i + 1)) refused.push({ email: rest, message });
      break;
    }
  }
  return refused;
}

/** One line per reason: the address it refused, or how many when the same reason refused several. */
export function refusalLines(refused: InviteRefusal[]): string[] {
  const byMessage = new Map<string, string[]>();
  for (const r of refused) byMessage.set(r.message, [...(byMessage.get(r.message) ?? []), r.email]);
  return [...byMessage].map(([message, emails]) =>
    emails.length === 1 ? `${emails[0]}: ${message}` : `${emails.length} not invited: ${message}`,
  );
}
